import React, { Fragment, useCallback, useState } from "react";
import WorkspaceItemPreviewActions from "./WorkspaceItemPreviewActions";
import { Table as STable, Loader } from "semantic-ui-react";
import WorkspaceContentHeader from "./WorkspaceContentHeader";
import WorkspaceContentRow from "./WorkspaceContentRow";
import { produce } from "immer";
import { Droppable, DragDropContext } from "react-beautiful-dnd";
import {
  DocumentFilePreview,
  DocumentImageLightbox,
} from "components/documents";
import { getFilePreviewLink } from "./actions";

const WorkspaceContentTable = ({
  tableProps,
  state,
  selectedLength,
  onChangeFilters,
  reordering,
  tableData,
  path,
  skipRoot,
  rootSource,
  events,
  setTableData,
  previewFileId,
  onOpenPreview,
  onClosePreview,
}) => {
  const [expandedImage, setExpandedImage] = useState(null);

  const onExpand = useCallback(
    (url, fileName) => setExpandedImage({ fileName, url }),
    []
  );

  const closePreview = () => {
    onClosePreview();
    events.onRefresh();
  };

  return (
    <Fragment>
      <DocumentImageLightbox
        images={expandedImage ? [expandedImage] : []}
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        targetSrc={expandedImage?.url}
      />
      <DocumentFilePreview
        open={!!previewFileId}
        onClose={closePreview}
        fileId={previewFileId}
        loadAction={getFilePreviewLink}
        onExpand={onExpand}
        closeOnEscape={!expandedImage}
        renderActions={(file) => (
          <WorkspaceItemPreviewActions
            item={file}
            directoryId={tableProps.data.id}
            siteRootDirectoryId={tableProps.data.siteRootDirectoryId}
            onDelete={closePreview}
          />
        )}
      />
      <STable>
        <WorkspaceContentHeader
          tableData={tableData}
          totalResults={tableProps.totalResults}
          state={state}
          selectedLength={selectedLength}
          onChangeFilters={onChangeFilters}
          reordering={reordering}
          events={events}
        />
        <DragDropContext
          onDragEnd={({ source, destination }) => {
            const sourceIndex = source.index;
            const destinationIndex = destination.index;
            if (sourceIndex !== destinationIndex) {
              const nextData = produce(tableData, (draft) => {
                const deleted = draft.splice(sourceIndex, 1);
                if (deleted.length) {
                  draft.splice(destinationIndex, 0, deleted[0]);
                }
              });
              setTableData(nextData);
            }
          }}
        >
          <Droppable droppableId="workspaceDroppable">
            {(provided, snapshot) => (
              <tbody {...provided.droppableProps} ref={provided.innerRef}>
                {!(tableData || []).length ? (
                  <STable.Row>
                    <STable.Cell colSpan={10} textAlign="center">
                      No files found
                    </STable.Cell>
                  </STable.Row>
                ) : state.loading || state.slowLoading ? (
                  <STable.Row>
                    <STable.Cell
                      colSpan={10}
                      style={{
                        minHeight: "80px",
                        textAlign: "center",
                      }}
                    >
                      <Loader size="large" active inline="centered" />
                    </STable.Cell>
                  </STable.Row>
                ) : (
                  (tableData || []).map((item, i) => {
                    return (
                      <WorkspaceContentRow
                        key={i}
                        index={i}
                        item={item}
                        path={path}
                        skipRoot={skipRoot}
                        rootSource={rootSource}
                        reordering={reordering}
                        state={state}
                        events={events}
                        tableProps={tableProps}
                        onPreview={onOpenPreview}
                      />
                    );
                  })
                )}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </STable>
    </Fragment>
  );
};

export default WorkspaceContentTable;
